"use client";
import Link from "next/link";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { DrupalNode, DrupalTaxonomyTerm } from "next-drupal";
import { motion } from "framer-motion";
import { FiSearch } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import slugify from "slugify";
import { IoClose, IoDocumentTextOutline } from "react-icons/io5";
import CommonMultiCheckox from "@/components/sections/CommonMultiCheckbox";
import FloatingButton from "@/components/ui/FloatingButton";
import InfiniteScroll from "react-infinite-scroll-component";
import ReactPlayer from "react-player";
import {
  buildMediaTypeAndSrc,
  createQueryString,
  formatDateToUS,
} from "@/utils";
import Banner from "@/components/sections/Banner";
import { config } from "@/lib/config";
import {
  COUNTRY_OPTIONS,
  SECTOR_OPTIONS,
  CATEGORY_OPTIONS,
  PAGE_OPTIONS,
  normalizeFilterValues,
} from "@/constants/news-filters";


interface newsProps {
  filteredNewsCards: DrupalNode[];
  resourcesFilteredData: DrupalTaxonomyTerm[];
  searchParams: any;
  selectedResources: string[];
  selectedCountries: string[];
  selectedCategories: string[];
  selectedSectors: string[];
  selectedPages: string[];
  showClearBtn: boolean;
}

const NewsScreen: React.FC<newsProps> = ({
  searchParams,
  filteredNewsCards,
  resourcesFilteredData,
  selectedResources,
  selectedCountries,
  selectedCategories,
  selectedSectors,
  selectedPages,
  showClearBtn,
}) => {
  const searchInputRef = useRef(null);
  const searchListRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [searchFocus, setSearchFocus] = useState<null | number>(null);
  const router = useRouter();
  const path = usePathname();
  const [isAboveMobile, setAboveMobile] = useState<boolean>(false);
  const [searchItem, setSearchItem] = useState<string>("");
  const [searchListOpen, setSearchListOpen] = useState<boolean>(false);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const [paginatedFilterNewsData, setPaginatedNewsData] = useState<
    DrupalNode[]
  >([]);
  const [isOpen, setIsOpen] = useState<boolean | undefined>(true);
  const [isCountryOpen, setIsCountryOpen] = useState<boolean | undefined>(
    selectedCountries.length > 0,
  );
  const [isCategoryOpen, setIsCategoryOpen] = useState<boolean | undefined>(
    selectedCategories.length > 0,
  );
  const [isSectorOpen, setIsSectorOpen] = useState<boolean | undefined>(
    selectedSectors.length > 0,
  );
  const [isPageOpen, setIsPageOpen] = useState<boolean | undefined>(
    selectedPages.length > 0,
  );
  const ITEMS_PER_PAGE = 6;
  const [itemsToShow, setItemsToShow] = useState(ITEMS_PER_PAGE);

  const withoutEvents = useMemo(() => {
    return filteredNewsCards.filter(
      (card) => card?.field_n_resource?.name !== "Event",
    );
  }, [filteredNewsCards]);

  //  sort the news according to date
  const allSortedNews = useMemo(() => {
    return [...withoutEvents].sort((a: any, b: any) => {
      const dateA: any = new Date(a.field_news_date || a.created);
      const dateB: any = new Date(b.field_news_date || b.created);
      return dateB - dateA;
    });
  }, [withoutEvents]);

  useEffect(() => {
    setSearchItem(searchParams.search);
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    setItemsToShow(ITEMS_PER_PAGE);
    const timeout = isAboveMobile ? 800 : 100;
    const timer = setTimeout(() => {
      setPaginatedNewsData(allSortedNews.slice(0, ITEMS_PER_PAGE));
      setLoading(false);
    }, timeout);
    return () => clearTimeout(timer);
  }, [allSortedNews, isAboveMobile]);

  const handleLoadMore = () => {
    const nextCount = itemsToShow + ITEMS_PER_PAGE;
    setItemsToShow(nextCount);
    setPaginatedNewsData(allSortedNews.slice(0, nextCount));
  };

  const makeResourcesFilteredOptions = () => {
    return resourcesFilteredData
      ?.filter((item) => item.name !== "Event")
      .map((data) => ({
        value: data.name,
        label: data.name,
      }));
  };

  const handleResourceSelect = (value: string[]) => {
    setLoading(true);
    const query = createQueryString({
      ...searchParams,
      resource: value,
    });
    router.push(`${path}${query}`, { scroll: false });
  };

  const handleSectorSelect = (value: string[]) => {
    setLoading(true);
    const query = createQueryString({
      ...searchParams,
      sector: value,
    });
    router.push(`${path}${query}`, { scroll: false });
  };

  const handleCategorySelect = (value: string[]) => {
    setLoading(true);
    const query = createQueryString({
      ...searchParams,
      category: value,
    });
    router.push(`${path}${query}`, { scroll: false });
  };

  const handleCountrySelect = (value: string[]) => {
    setLoading(true);
    const isIndiaSelected = value.some((c) => c.toLowerCase() === "india");
    const query = createQueryString({
      ...searchParams,
      country: value,
      sector: isIndiaSelected ? searchParams.sector : [],
    });
    router.push(`${path}${query}`, { scroll: false });
  };

  const handlePageSelect = (value: string[]) => {
    setLoading(true);
    const query = createQueryString({
      ...searchParams,
      page: value,
    });
    router.push(`${path}${query}`, { scroll: false });
  };

  //  search filter
  const handleSearch = () => {
    setLoading(true);
    let searchValue = searchItem;
    if (searchFocus !== null) {
      searchValue = (searchListRef.current as any).childNodes[searchFocus]
        .innerText;
      setSearchFocus(null);
    }
    const query = createQueryString({
      ...searchParams,
      search: searchValue.trim(),
    });
    router.push(`${path}${query}`, { scroll: false });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value) {
      setSearchItem(event.target.value.trimStart());
      setSearchListOpen(true);
    } else {
      handleClearSearch();
      setLoading(false);
    }
  };

  const filteredSearchData = allSortedNews?.filter((item) =>
    item.title.toLowerCase().includes(searchItem?.toLowerCase()),
  );

  // clear search filter
  const handleClearSearch = () => {
    setLoading(true);
    const query = createQueryString({
      ...searchParams,
      search: "",
    });
    setLoading(false); // look for better fix :: clear button after typing, keeps loading data
    router.push(`${path}${query}`, { scroll: false });
    setSearchItem("");
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window?.innerWidth > 767) {
        setAboveMobile(true);
      }
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = showMobileFilters ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showMobileFilters]);

  return (
    <div className="pt-20 bg-mapGray filter-page-height">
      <Banner
        title="News & Publications"
        isRounded
        backgroundImg="/static/images/news-banner.webp"
      />

      <FloatingButton
        onClick={() => setShowMobileFilters(!showMobileFilters)}
      />
      <div className="flex justify-between py-[60px] betweenMobileTab:pt-14 betweenMobileTab:pb-14 mobileMax:pt-8 mobileMax:pb-12 px-8 lieTablets:px-3 largeDesk:px-2 mobileMax:flex-wrap mobileMax:px-[12px] exactLaptop:min-h-[450px]">
        {showMobileFilters && (
          <div
            className="hidden lieTablets:block fixed h-full w-full bg-blackHighOpacity z-[4] top-0 right-0"
            onClick={() => setShowMobileFilters(false)}
          />
        )}
        <div
          className={`${
            showMobileFilters
              ? "lieTablets:w-[300px] lieTablets:left-0 lieTablets:top-0 fixed left-0 top-0 flex-col mb-0 !z-50 w-full h-full dark-filter-shadow !block overflow-auto"
              : "sticky top-24 left-0 mr-2 mb px-5 w-[25%] desktopLg:w-[20%]"
          } aboveLaptop:w-[30%] mobileMax:w-full z-[3] h-full hidden exactLaptop:block`}
        >
          <div className="h-full bg-mapGray w-full laptopMax:h-full laptopMax:overflow-auto">
            <div className="flex laptopMax:px-3 items-center justify-between laptopMax:pt-[40px] laptopMax:sticky laptopMax:top-0 laptopMax:z-[3] bg-mapGray">
              <IoClose
                className="exactLaptop:hidden text-odd text-black absolute right-2.5 top-1.5 cursor-pointer"
                onClick={() => setShowMobileFilters(false)}
              />
              <h5 className="--font-poppins text-medium leading-normal text-black mobileMax:text-small font-bold">
                Filter by:
              </h5>
              <div className="flex items-center">
                {showClearBtn && (
                  <button
                    className="block capitalize rounded-full ml-2 px-5 largeDesk:px-3 py-1 border border-borderColor mobileMax:text-xsmall text-small --font-poppins flex items-center justify-center cursor-pointer text-black"
                    onClick={() => {
                      if (isAboveMobile) {
                        window.scrollTo({ top: 400, behavior: "smooth" });
                      }
                      const { search = "" } = searchParams;
                      const query = createQueryString({
                        search,
                      });
                      router.push(`${path}${query}`, { scroll: false });
                    }}
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
            <div className="w-full mobileMax:mt-3 mt-[6px] custom-react-combbox">
              <div className="bg-white commonBoxShadow w-full mt-5 mobileMax:mt-3 border-box max-h-[75vh] lieTablets:max-h-[80vh] mobileMax:max-h-[80vh] overflow-auto">
                <div className="w-full border-b border-b-[#e7e7e7]">
                  <CommonMultiCheckox
                    singleMenu={true}
                    menuTitle="Resources"
                    value={selectedResources.map((res) => {
                      const found = makeResourcesFilteredOptions()?.find(
                        (opt) => opt.value.toLowerCase() === res.toLowerCase(),
                      );
                      return found ? found.value : res;
                    })}
                    list={makeResourcesFilteredOptions()}
                    onSelectChange={handleResourceSelect}
                    isOpen={isOpen}
                    toggleOpen={() => setIsOpen((p) => !p)}
                  />
                </div>
                <div className="w-full border-b border-b-[#e7e7e7]">
                  <CommonMultiCheckox
                    singleMenu={true}
                    menuTitle="Country"
                    value={normalizeFilterValues(selectedCountries, COUNTRY_OPTIONS)}
                    list={COUNTRY_OPTIONS}
                    onSelectChange={handleCountrySelect}
                    isOpen={isCountryOpen}
                    toggleOpen={() => setIsCountryOpen((p) => !p)}
                  />
                </div>
                {selectedCountries.some((c) => c.toLowerCase() === "india") && (
                  <div className="w-full border-b border-b-[#e7e7e7]">
                    <CommonMultiCheckox
                      singleMenu={true}
                      menuTitle="Sector"
                      value={normalizeFilterValues(selectedSectors, SECTOR_OPTIONS)}
                      list={SECTOR_OPTIONS}
                      onSelectChange={handleSectorSelect}
                      isOpen={isSectorOpen}
                      toggleOpen={() => setIsSectorOpen((p) => !p)}
                    />
                  </div>
                )}
                <div className="w-full border-b border-b-[#e7e7e7]">
                  <CommonMultiCheckox
                    singleMenu={true}
                    menuTitle="Category"
                    value={normalizeFilterValues(selectedCategories, CATEGORY_OPTIONS)}
                    list={CATEGORY_OPTIONS}
                    onSelectChange={handleCategorySelect}
                    isOpen={isCategoryOpen}
                    toggleOpen={() => setIsCategoryOpen((p) => !p)}
                  />
                </div>
                <div className="w-full border-b border-b-[#e7e7e7]">
                  <CommonMultiCheckox
                    singleMenu={true}
                    menuTitle="Page"
                    value={normalizeFilterValues(selectedPages, PAGE_OPTIONS)}
                    list={PAGE_OPTIONS}
                    onSelectChange={handlePageSelect}
                    isOpen={isPageOpen}
                    toggleOpen={() => setIsPageOpen((p) => !p)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-[75%] desktopLg:w-[80%] betweenMobileTab:w-[70%] lieTablets:w-full laptopMax:w-full">
          {/* no data found */}
          <div className="w-full flex items-center justify-between mobileMax:block mobileMax:mb-0 ml-[15px] laptopMax:ml-0">
            <div className="relative w-[48%] mobileMax:w-full mobileMax:mb-8">
              <div className="flex items-center justify-end relative w-full">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  className="searchInput text-small --font-poppins mobileMax:text-xsmall"
                  value={searchItem}
                  onChange={handleChange}
                  onKeyDown={(event) => {
                    if (searchItem?.trim?.()?.length) {
                      if (event.key === "Enter") {
                        handleSearch();
                        setSearchListOpen(false);
                        (searchInputRef.current as any)?.blur();
                      }
                      if (
                        event.key === "ArrowUp" ||
                        event.key === "ArrowDown"
                      ) {
                        event.preventDefault();
                        if (searchFocus !== null) {
                          const newIndex =
                            event.key === "ArrowUp"
                              ? Math.max(0, searchFocus - 1)
                              : Math.min(
                                  filteredSearchData.length - 1,
                                  searchFocus + 1,
                                );
                          setSearchFocus(newIndex);
                        } else {
                          setSearchFocus(0);
                        }
                      }
                    }
                  }}
                />
                <FiSearch
                  className="absolute text-[21px] right-[10px] cursor-pointer text-[#5e5e5e]"
                  onClick={() => {
                    if (searchItem?.trim?.()?.length) {
                      handleSearch();
                      setSearchListOpen(false);
                    }
                  }}
                />
                {searchItem && (
                  <IoMdClose
                    className="absolute text-medium right-[35px] cursor-pointer text-[#000]"
                    onClick={handleClearSearch}
                  />
                )}
              </div>
              {searchItem && searchListOpen && (
                <ul
                  ref={searchListRef}
                  className="absolute py-3 px-1 top-[40px] z-10 bg-white card-shadow rounded-lg w-full"
                >
                  {filteredSearchData?.length ? (
                    filteredSearchData.map(
                      (matchedItem: DrupalNode, index: number) => {
                        return (
                          <li
                            key={index}
                            className={`text-small px-2 rounded-[6px] leading-normal py-1 mobileMax:text-xsmall cursor-pointer search-item ${
                              index === searchFocus ? "focused" : ""
                            }`}
                            onClick={() => {
                              setSearchListOpen(false);
                              setSearchItem(matchedItem?.title);
                              const query = createQueryString({
                                ...searchParams,
                                search: matchedItem?.title,
                              });
                              router.push(`${path}${query}`);
                            }}
                          >
                            {matchedItem?.title}
                          </li>
                        );
                      },
                    )
                  ) : (
                    <p className="text-small leading-normal text-center py-3 mobileMax:text-xsmall">
                      No Data Found
                    </p>
                  )}
                </ul>
              )}
            </div>
            {!loading ? (
              <h5 className="--font-poppins text-[16px] uppercase tracking-[0.5px] leading-normal mobileMax:text-small font-semibold">
                {allSortedNews?.length}
                <span>&nbsp;results</span>
              </h5>
            ) : (
              <p className="h-[30px]" />
            )}
          </div>
          {!loading ? (
            <>
              {!allSortedNews?.length && !loading ? (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  className="remove-animation-fluctuation h-full laptop:min-h-[150px] laptopMax:h-[250px] flex items-center justify-center"
                >
                  <p className="text-xmedium text-center --font-poppins">
                    No Data Found.
                  </p>
                </motion.div>
              ) : (
                <div>
                  <InfiniteScroll
                    scrollThreshold={0.5}
                    className="pt-[20px]"
                    dataLength={paginatedFilterNewsData?.length}
                    hasMore={
                      paginatedFilterNewsData?.length < allSortedNews.length
                    }
                    next={handleLoadMore}
                    loader={
                      <div className="w-full flex justify-center py-4">
                        <p className="text-small font-poppins">
                          Loading more...
                        </p>
                      </div>
                    }
                  >
                    <div className="flex flex-wrap justify-start box-border w-full">
                      {paginatedFilterNewsData?.map(
                        (newsData: DrupalNode, index: number) => {
                          const sluggedLink = newsData.title
                            ? `/news/${slugify(`${newsData.title} ${newsData.id}`, { lower: true, strict: true })}`
                            : `/news/${newsData.id}`;

                          const mediaTypeAndSrc = buildMediaTypeAndSrc(
                            newsData.field_news_media_url,
                          );
                          const redirectLink = newsData.field_news_link || "#";

                          return (
                            <Link
                              key={index}
                              href={
                                newsData?.field_mark_as_external_source
                                  ? redirectLink
                                  : sluggedLink || "#"
                              }
                              target={
                                newsData?.field_mark_as_external_source
                                  ? "_blank"
                                  : "_self"
                              }
                              className="desktop:px-[15px] desktop:w-[50%] mb-8 w-full betweenMobileTab:mb-6 laptopMax:mb-0 laptopMax:py-3 laptopMax:border-t-0"
                            >
                              {/* INNER WRAPPER */}
                              <motion.div
                                className="flex items-start w-full h-[200px] aboveMinMobile:h-[160px] minMobile:h-[140px] bg-white rounded-[4px] overflow-hidden card-shadow safari-ios-fix"
                                whileHover={{ scale: 1.02 }}
                                transition={{ duration: 0 }}
                              >
                                {/* MEDIA */}
                                <div className="tab:w-[40%] tab:max-w-[50%] flex justify-center items-center w-full overflow-hidden relative mobileMax:mb-0 h-full minMobile:w-[90%] mobileMax:mr-2 lieTablets:mr-3">
                                  {mediaTypeAndSrc.type === "image" ||
                                  newsData.field_image?.uri?.url ? (
                                    <img
                                      src={
                                        newsData.field_image?.uri?.url
                                          ? `${config.apiBase}${newsData.field_image?.uri?.url}`
                                          : mediaTypeAndSrc.src || ""
                                      }
                                      alt=""
                                      className="w-full h-full max-w-full object-cover card-shadow"
                                    />
                                  ) : mediaTypeAndSrc.type === "video" ? (
                                    <div className="w-full h-full">
                                      <ReactPlayer
                                        url={mediaTypeAndSrc?.src || ""}
                                        width="100%"
                                        height="100%"
                                        light={
                                          <div className="w-full h-full relative">
                                            <img
                                              src={`https://img.youtube.com/vi/${(() => {
                                                const regExp =
                                                  /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                                                const match =
                                                  mediaTypeAndSrc?.src?.match(
                                                    regExp,
                                                  );
                                                return match &&
                                                  match[2].length === 11
                                                  ? match[2]
                                                  : mediaTypeAndSrc?.src
                                                      ?.split("/")
                                                      .pop();
                                              })()}/maxresdefault.jpg`}
                                              onError={(e) => {
                                                const target =
                                                  e.target as HTMLImageElement;
                                                if (
                                                  !target.src.includes(
                                                    "hqdefault.jpg",
                                                  )
                                                ) {
                                                  target.src =
                                                    target.src.replace(
                                                      "maxresdefault.jpg",
                                                      "hqdefault.jpg",
                                                    );
                                                  target.classList.add(
                                                    "scale-[1.15]",
                                                  );
                                                }
                                              }}
                                              alt="video thumbnail"
                                              className="w-full h-full object-cover transition-transform duration-500"
                                            />
                                          </div>
                                        }
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-full h-full bg-placeholder flex items-center justify-center">
                                      {mediaTypeAndSrc.src ? (
                                        <img
                                          src={mediaTypeAndSrc.src}
                                          alt=""
                                          className="w-full h-full max-w-full object-cover card-shadow"
                                        />
                                      ) : (
                                        <IoDocumentTextOutline className="text-white w-[65%] h-[65%]" />
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* CONTENT */}
                                <div className="exactLaptop:w-[60%] exactLaptop:p-4 flex flex-col h-full w-full laptopMax:py-0.5 min-w-0">
                                  <div className="w-full h-full flex flex-col justify-between">
                                    <div className="min-w-0">
                                      {/* TITLE */}
                                      <h4 className="text-numans mb-3 block text-left text-[18px] mobileMax:text-small text-cardHeading line-clamp-2 aboveMinMobile:line-clamp-3 font-semibold hover:text-[#003350] hover:underline leading-normal mobileMax:h-auto capitalize">
                                        {newsData?.title}
                                      </h4>

                                      {/* DESCRIPTION */}
                                      <div className="safari-clamp-2 text-small text-cardText leading-6 mobileMax:text-xsmall mobileMax:leading-normal">
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html:
                                              newsData?.field_news_blurb?.value,
                                          }}
                                        />
                                      </div>
                                    </div>

                                    {/* META */}
                                    <div className="flex items-start flex-wrap mt-auto">
                                      {newsData?.field_news_date && (
                                        <p className="text-xsmall mobileMax:text-[13px] text-gray font-medium leading-6 --font-poppins mr-2 mobileMax:mr-2 flex items-center">
                                          {formatDateToUS(
                                            newsData.field_news_date,
                                          )}
                                        </p>
                                      )}
                                      <div
                                        className={`${newsData?.field_news_date ? "pl-2 border-l-[1px] border-gray " : ""} text-gray text-xsmall mobileMax:text-[13px] font-medium leading-6 --font-poppins`}
                                      >
                                        {newsData?.field_n_resource?.name}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            </Link>
                          );
                        },
                      )}
                    </div>
                  </InfiniteScroll>
                </div>
              )}
            </>
          ) : (
            <div className="pt-[20px] flex w-full justify-start flex-wrap">
              {/* placeholder Ui */}
              {Array(8)
                .fill(0)
                ?.map((_, index: number) => {
                  return (
                    <div
                      key={index}
                      className="desktop:px-[15px] desktop:w-[50%] mb-8 w-full betweenMobileTab:mb-6 laptopMax:mb-0 laptopMax:py-3 laptopMax:border-t-[1px] laptopMax:border-[#8A8C8E]"
                    >
                      <div className="flex items-start box-border exactLaptop:bg-white remove-news-shadow card-shadow w-full h-[200px] aboveMinMobile:h-[160px] minMobile:h-[140px] exactLaptop:rounded-[4px] overflow-hidden">
                        <div className="tab:w-[40%] tab:max-w-[50%] flex justify-center items-center w-full overflow-hidden relative mobileMax:mb-0 h-full minMobile:w-[90%] mobileMax:mr-2 lieTablets:mr-3">
                          <div className="w-full h-full bg-emptyState animate-pulse" />
                        </div>
                        <div className="exactLaptop:w-[60%] exactLaptop:p-4 flex flex-col h-full w-full laptopMax:py-0.5">
                          <div className="w-full h-full flex flex-col justify-between">
                            <div className="h-[85%]">
                              <div className="bg-emptyState animate-pulse rounded-md w-full mb-2.5 h-[40px]" />
                              <div className="bg-emptyState animate-pulse rounded-md w-full mb-4 h-[30px]" />
                            </div>
                            <div className="w-full h-[30px] mb-0.5 flex items-center">
                              <div className="bg-emptyState animate-pulse rounded-md mr-2 h-[25px] w-[30%] laptopMax:w-[40%]" />
                              <div className="bg-emptyState animate-pulse rounded-md mr-2 h-[25px] w-[30%] laptopMax:w-[40%]" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsScreen;
